import { ErpDiv, ErpButtonRaw } from './ErpHtml';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface ErpAlertProps {
  modelValue?: boolean;
  variant?: string;
  dismissible?: boolean;
  show?: boolean;
  onUpdateModelValue?: (v: boolean) => void;
  onDismissed?: () => void;
  children?: React.ReactNode;
  className?: string;
}

const ErpAlert: React.FC<ErpAlertProps> = ({
  modelValue,
  variant = 'info',
  dismissible,
  show,
  onUpdateModelValue,
  onDismissed,
  children,
  className = '',
}) => {
  const { t } = useTranslation();
  const [internal, setInternal] = useState(modelValue ?? show ?? true);

  const isVisible = modelValue !== undefined ? modelValue : internal;

  const handleSetVisible = (v: boolean) => {
    if (modelValue !== undefined && onUpdateModelValue) {
      onUpdateModelValue(v);
    } else {
      setInternal(v);
    }
  };

  const dismiss = () => {
    handleSetVisible(false);
    if (onDismissed) {
      onDismissed();
    }
  };

  useEffect(() => {
    if (modelValue !== undefined) {
      setInternal(modelValue);
    }
  }, [modelValue]);

  useEffect(() => {
    if (show !== undefined) {
      setInternal(show);
    }
  }, [show]);

  if (!isVisible) return null;

  const variantClasses: Record<string, string> = {
    info: 'bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-800',
    success: 'bg-green-50 text-green-900 border-green-200 dark:bg-green-950 dark:text-green-100 dark:border-green-800',
    danger: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-950 dark:text-red-100 dark:border-red-800',
    error: 'bg-red-50 text-red-900 border-red-200 dark:bg-red-950 dark:text-red-100 dark:border-red-800',
    warning: 'bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-950 dark:text-amber-100 dark:border-amber-800',
    primary: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-800',
    secondary: 'bg-muted text-foreground border-border',
    dark: 'bg-slate-800 text-slate-100 border-slate-700',
  };

  const vClass = variantClasses[variant] || variantClasses.info;
  const alertClasses = `relative min-w-0 p-4 mb-4 border rounded-md flex justify-between items-start gap-3 ${vClass} ${className}`;

  return (
    <ErpDiv className={alertClasses} role="alert">
      <ErpDiv className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">{children}</ErpDiv>
      {dismissible && (
        <ErpButtonRaw
          type="button"
          className="-mt-1 shrink-0 rounded text-current hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current text-xl leading-none"
          onClick={dismiss}
          aria-label={t('Close')}
        >
          &times;
        </ErpButtonRaw>
      )}
    </ErpDiv>
  );
};

export default ErpAlert;
